import * as React from 'react'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { TaskType } from '@/app/constants/taskType'
import { useTaskTrackerStore } from '@/modules/shared/stores/taskTrackerStore'

const TASK_WATCHDOG_MS = 5 * 60 * 1000


const TASK_REGISTRATION_GRACE_MS = 10_000

type PendingTask = {
  id: string
  registeredAt: number
  seenInQueue: boolean
}

type State = {
  runningJobs: number
  pendingTasks: PendingTask[]
}

type Actions = {
  beginJob: () => () => void
  trackTask: (taskId: string) => void
  syncWithTaskQueue: (liveTaskIds: Set<string>) => void
  reset: () => void
}

export const usePrintJobStore = create<State & Actions>()(
  devtools(
    (set, get) => ({
      runningJobs: 0,
      pendingTasks: [],

      beginJob: () => {
        set((s) => ({ runningJobs: s.runningJobs + 1 }), false, 'beginJob')

        let released = false
        return () => {
          if (released) return
          released = true
          set(
            (s) => ({ runningJobs: Math.max(0, s.runningJobs - 1) }),
            false,
            'endJob',
          )
        }
      },

      trackTask: (taskId) => {
        if (!taskId) return
        if (get().pendingTasks.some((task) => task.id === taskId)) return

        set(
          (s) => ({
            pendingTasks: [
              ...s.pendingTasks,
              { id: taskId, registeredAt: Date.now(), seenInQueue: false },
            ],
          }),
          false,
          'trackTask',
        )

        watchTaskQueue()
      },

      syncWithTaskQueue: (liveTaskIds) => {
        const { pendingTasks } = get()
        if (pendingTasks.length === 0) return

        const now = Date.now()
        let changed = false

        const next = pendingTasks.reduce<PendingTask[]>((acc, task) => {
          if (now - task.registeredAt > TASK_WATCHDOG_MS) {
            changed = true
            return acc
          }

          if (liveTaskIds.has(task.id)) {
            acc.push(task.seenInQueue ? task : { ...task, seenInQueue: true })
            if (!task.seenInQueue) changed = true
            return acc
          }

         
          if (
            !task.seenInQueue &&
            now - task.registeredAt < TASK_REGISTRATION_GRACE_MS
          ) {
            acc.push(task)
            return acc
          }

          changed = true
          return acc
        }, [])

        if (!changed) return
        set({ pendingTasks: next }, false, 'syncWithTaskQueue')
      },

      reset: () => set({ runningJobs: 0, pendingTasks: [] }, false, 'reset'),
    }),
    { name: 'EasyTacho', store: 'print-job' },
  ),
)

export const selectIsPrintJobRunning = (state: State) =>
  state.runningJobs > 0 || state.pendingTasks.length > 0

export const isPrintJobRunning = () =>
  selectIsPrintJobRunning(usePrintJobStore.getState())

export const usePrintJobRunning = () =>
  usePrintJobStore(selectIsPrintJobRunning)

/**
 * Licznik postepu dla nakladki generowania. Sam printJobStore wie tylko, ZE cos leci -
 * liczby przychodza z pollingu zadania w taskTrackerStore, wiec trzeba je zestawic po id.
 * Zwraca null, dopoki zadanie nie zdazylo sie zarejestrowac (wtedy zostaje sam spinner).
 */
export type PrintJobProgress = {
  progressCurrent: number
  progressTotal: number
  /** Znacznik fazy z serwera - patrz `taskProgressLabel`. */
  message?: string
  /** Do wyliczenia czasu trwania w fazie, ktora nie ma licznika. */
  createdAt?: string
}

export const usePrintJobProgress = (): PrintJobProgress | null => {
  const pendingTasks = usePrintJobStore((s) => s.pendingTasks)
  const trackedTasks = useTaskTrackerStore((s) => s.tasks)

  return React.useMemo(() => {
    for (const pending of pendingTasks) {
      const task = trackedTasks[pending.id]
      if (task?.progressTotal) {
        return {
          progressCurrent: task.progressCurrent ?? 0,
          progressTotal: task.progressTotal,
          message: task.message,
          createdAt: task.createdAt,
        }
      }
    }
    return null
  }, [pendingTasks, trackedTasks])
}

const readLivePrintTaskIds = (): Set<string> => {
  try {
    const tasks = Object.values(useTaskTrackerStore.getState().tasks)
    return new Set(
      tasks
        .filter((task) => task.type === TaskType.Printout)
        .map((task) => task.id),
    )
  } catch {
    return new Set()
  }
}

let unsubscribeFromTracker: (() => void) | null = null
let watchdogInterval: ReturnType<typeof setInterval> | null = null


const watchTaskQueue = () => {
  const sync = () => {

    const state = usePrintJobStore.getState()
    if (state.pendingTasks.length === 0) return
    state.syncWithTaskQueue(readLivePrintTaskIds())
  }

  if (!unsubscribeFromTracker) {
    try {
      unsubscribeFromTracker = useTaskTrackerStore.subscribe(sync)
    } catch {
      unsubscribeFromTracker = null
    }
  }


  if (!watchdogInterval) {
    watchdogInterval = setInterval(() => {
      sync()
      if (usePrintJobStore.getState().pendingTasks.length === 0) {
        clearInterval(watchdogInterval!)
        watchdogInterval = null
      }
    }, 1000)
  }
}
